// client/src/pages/SignupPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Form, Button, Card, Spinner, Row, Col, Image, InputGroup
} from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import logo from '../assets/logo-c.png';

const API_ROOT_URL = process.env.REACT_APP_API_ROOT_URL;

function SignupPage() {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    accountId: ''
  });

  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [accountRole, setAccountRole] = useState('');
  const [accountUsernameRef, setAccountUsernameRef] = useState('');
  const [isAccountIdValid, setIsAccountIdValid] = useState(false);
  const [accountIdLoading, setAccountIdLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { register, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      const targetPath = user?.role === 'Admin' ? '/orders' : '/';
      navigate(targetPath, { replace: true });
    }
  }, [isAuthenticated, navigate, user]);

  // Stable email regex
  const emailRegex = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i, []);

  // ====== Field validators ======
  const validateField = useCallback((name, value, data) => {
    switch (name) {
      case 'accountId':
        if (!value.trim()) return 'Account ID is required.';
        if (!isAccountIdValid) return 'Account ID is invalid.';
        return '';
      case 'username': {
        const v = value.trim();
        if (!v) return 'Name is required.';
        if (v.length < 3) return 'Name must be at least 3 characters.';
        return '';
      }
      case 'email': {
        const v = value.trim();
        if (!v) return 'Email is required.';
        if (!emailRegex.test(v)) return 'Invalid email address.';
        return '';
      }
      case 'password':
        if (!value) return 'Password is required.';
        if (value.length < 6) return 'Must be at least 6 characters.';
        return '';
      case 'confirmPassword':
        if (data.password !== value) return 'Passwords do not match.';
        return '';
      default:
        return '';
    }
  }, [emailRegex, isAccountIdValid]);

  const validateAll = useCallback((data) => {
    const nextErrors = {
      accountId: validateField('accountId', data.accountId, data),
      username: validateField('username', data.username, data),
      email: validateField('email', data.email, data),
      password: validateField('password', data.password, data),
      confirmPassword: validateField('confirmPassword', data.confirmPassword, data)
    };
    setErrors(nextErrors);
    return Object.values(nextErrors).every((e) => !e);
  }, [validateField]);

  // ====== Account ID async validation ======
  const performAccountIdValidation = useCallback(async (id) => {
    if (!id) {
      setIsAccountIdValid(false);
      setAccountRole('');
      setAccountUsernameRef('');
      setMessage('');
      setMessageType('');
      setAccountIdLoading(false);
      setErrors(prev => ({ ...prev, accountId: touched.accountId ? 'Account ID is required.' : '' }));
      return;
    }

    setAccountIdLoading(true);
    setMessage('');
    setMessageType('');

    try {
      const response = await fetch(`${API_ROOT_URL}/accounts/${id}`);
      const result = await response.json();

      if (response.ok) {
        setIsAccountIdValid(true);
        setAccountRole(result.role);
        setAccountUsernameRef(result.username || 'Not specified by admin');
        setMessage('Account ID is valid. You will be registered as a ' + result.role + '.');
        setMessageType('success');
        setErrors(prev => ({ ...prev, accountId: '' }));
      } else {
        setIsAccountIdValid(false);
        setAccountRole('');
        setAccountUsernameRef('');
        setMessage(result.message || 'Invalid or already registered Account ID.');
        setMessageType('danger');
        setErrors(prev => ({
          ...prev,
          accountId: touched.accountId ? (result.message || 'Account ID is invalid.') : ''
        }));
      }
    } catch (error) {
      console.error('Error validating account ID:', error);
      setIsAccountIdValid(false);
      setAccountRole('');
      setAccountUsernameRef('');
      setMessage('Network error while validating Account ID.');
      setMessageType('danger');
      setErrors(prev => ({
        ...prev,
        accountId: touched.accountId ? 'Network error while validating Account ID.' : ''
      }));
    } finally {
      setAccountIdLoading(false);
    }
  }, [touched.accountId]);

  const handleAccountIdSearch = () => {
    performAccountIdValidation(formData.accountId.trim());
  };

  // ====== Handlers ======
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => {
      const next = { ...prev, [name]: value };

      if (name === 'password') {
        setErrors(prevErrs => ({
          ...prevErrs,
          password: validateField('password', value, next),
          confirmPassword: touched.confirmPassword ? validateField('confirmPassword', next.confirmPassword, next) : prevErrs.confirmPassword
        }));
      } else if (name === 'confirmPassword') {
        setErrors(prevErrs => ({
          ...prevErrs,
          confirmPassword: validateField('confirmPassword', value, next)
        }));
      } else if (name === 'email') {
        setErrors(prevErrs => ({
          ...prevErrs,
          email: validateField('email', value, next)
        }));
      } else if (name === 'username') {
        setErrors(prevErrs => ({
          ...prevErrs,
          username: validateField('username', value, next)
        }));
      } else if (name === 'accountId') {
          setIsAccountIdValid(false);
          setAccountRole('');
          setAccountUsernameRef('');
          setErrors(prevErrs => ({
            ...prevErrs,
            accountId: touched.accountId && !value.trim() ? 'Account ID is required.' : ''
          }));
          setMessage('');
          setMessageType('');
        }

      return next;
    });
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));

    setErrors(prev => ({
      ...prev,
      [field]: validateField(field, formData[field], formData)
    }));

    // Special handling for accountId
    if (field === 'accountId') {
      if (!formData.accountId.trim()) {
        setErrors(prev => ({ ...prev, accountId: 'Account ID is required.' }));
      } else if (!isAccountIdValid) {
        setErrors(prev => ({ ...prev, accountId: 'Account ID is invalid.' }));
      }
    }

    // Special handling for password -> revalidate confirmPassword if touched
    if ((field === 'password' || field === 'confirmPassword') && touched.confirmPassword) {
      setErrors(prev => ({
        ...prev,
        confirmPassword: validateField('confirmPassword', formData.confirmPassword, formData)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setMessageType('');

    setTouched({
      username: true,
      email: true,
      password: true,
      confirmPassword: true,
      accountId: true
    });

    const ok = validateAll(formData);
    if (!ok) return;

    setLoading(true);
    const { username, email, password, accountId } = formData;

    const result = await register({ username, email, password, accountId });

    if (result.success) {
      setMessage(result.message || 'Registration successful. Redirecting to login...');
      setMessageType('success');
      setFormData({
        username: '', email: '', password: '', confirmPassword: '', accountId: ''
      });
      setTouched({});
      setErrors({});
      setIsAccountIdValid(false);
      setAccountRole('');
      setAccountUsernameRef('');
      setRegistrationSuccess(true);

      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } else {
      setMessage(result.message || 'Registration failed.');
      setMessageType('danger');
    }
    setLoading(false);
  };

  return (
    <div
      className="min-vh-100 d-flex flex-column justify-content-center align-items-center p-3"
      style={{ background: 'linear-gradient(#4594ad, white, white)' }}
    >
      <div className="text-center mb-0 mt-0">
        <Image
          src={logo}
          alt="Company Logo"
          className="mb-3 mt-5 img-fluid"
          style={{ maxWidth: '250px', height: 'auto' }}
        />
        <h1 className="mt-5 mb-0 text-black">Touchless Tracking</h1>
      </div>

      <Card className="p-4 mt-4 shadow rounded border" style={{ maxWidth: '500px', width: '100%' }}>
        <Card.Body>
          <h4 className="text-center mb-4">Sign Up</h4>

          {message && (
            <div className={`alert alert-${messageType} text-center`} role="alert">
              {message}
            </div>
          )}

          <Form noValidate onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="formAccountId">
              <Form.Label>Enter your provided Account ID</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  name="accountId"
                  value={formData.accountId}
                  onChange={handleChange}
                  onBlur={() => handleBlur('accountId')}
                  disabled={loading || registrationSuccess}
                  placeholder="Enter Account ID"
                />
                <Button
                  variant="outline-primary"
                  onClick={handleAccountIdSearch}
                  disabled={!formData.accountId.trim() || loading || accountIdLoading || registrationSuccess}
                >
                  {accountIdLoading ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Checking...
                    </>
                  ) : (
                    'Search'
                  )}
                </Button>
              </InputGroup>
            </Form.Group>

            {isAccountIdValid && accountRole && (
              <Form.Group className="mb-3">
                <Form.Label>Assigned Role</Form.Label>
                <Form.Control plaintext readOnly value={accountRole} className="fw-bold text-center" />
                <Form.Text className="text-muted">Reference: {accountUsernameRef}</Form.Text>
              </Form.Group>
            )}

            <Form.Group className="mb-3" controlId="formUsername">
              <Form.Label>Full Name</Form.Label>
              <Form.Control
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                onBlur={() => handleBlur('username')}
                isInvalid={touched.username && !!errors.username}
                disabled={loading || !isAccountIdValid || registrationSuccess}
                minLength={3}
              />
              <Form.Control.Feedback type="invalid">{errors.username}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formEmail">
              <Form.Label>Email Address</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={() => handleBlur('email')}
                isInvalid={touched.email && !!errors.email}
                disabled={loading || !isAccountIdValid || registrationSuccess}
                pattern="^[^\s@]+@[^\s@]+\.[^\s@]{2,}$"
                title="Enter a valid email address (e.g., name@example.com)"
              />
              <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
            </Form.Group>

            <Row>
              {/* Password Field */}
              <Form.Group as={Col} className="mb-3" controlId="formPassword">
                <Form.Label>Password</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={() => handleBlur('password')}
                    isInvalid={touched.password && !!errors.password}
                    disabled={loading || !isAccountIdValid || registrationSuccess}
                    minLength={6}
                    placeholder="Enter password"
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowPassword(prev => !prev)}
                    disabled={loading || !isAccountIdValid || registrationSuccess}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </Button>
                  <Form.Control.Feedback type="invalid">
                    {errors.password}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>

              {/* Confirm Password Field */}
              <Form.Group as={Col} className="mb-3" controlId="formConfirmPassword">
                <Form.Label>Confirm Password</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    onBlur={() => handleBlur('confirmPassword')}
                    isInvalid={touched.confirmPassword && !!errors.confirmPassword}
                    disabled={loading || !isAccountIdValid || registrationSuccess}
                    placeholder="Confirm password"
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowConfirmPassword(prev => !prev)}
                    disabled={loading || !isAccountIdValid || registrationSuccess}
                  >
                    {showConfirmPassword ? 'Hide' : 'Show'}
                  </Button>
                  <Form.Control.Feedback type="invalid">
                    {errors.confirmPassword}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Row>

            <Button
              variant={registrationSuccess ? 'secondary' : 'primary'}
              type="submit"
              className="w-100 mt-3 fw-bold"
              disabled={loading || !isAccountIdValid || registrationSuccess}
            >
              {loading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                  <span className="ms-2">Registering...</span>
                </>
              ) : registrationSuccess ? (
                'Redirecting...'
              ) : (
                'Register'
              )}
            </Button>
          </Form>

          <Row className="mt-4">
            <Col className="text-center">
              <small>
                Already have an account?{' '}
                <a href="/login" className="fw-bold text-decoration-none">Login</a>
              </small>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
}

export default SignupPage;
